"use client"

import { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Text, TextInput, useTheme, Tooltip, IconButton } from "react-native-paper"
import { useUnits } from "../contexts/UnitsContext"
import convert from "convert-units"
import { getMeasureType } from "../utils/unitUtils"
import { FileIcon as FilePdf } from "lucide-react"

interface LabeledValueProps {
  labelDisplayName: string
  value: string | number
  units?: string
  testId?: string
  PDFPath?: string
}

export function LabeledValue({ labelDisplayName, value, units, testId, PDFPath }: LabeledValueProps) {
  const theme = useTheme()
  const { activeUnits } = useUnits()

  // Convert value if units are provided
  const displayValue = useMemo(() => {
    if (typeof value !== "number" || !units) return value

    const measureType = getMeasureType(units)
    if (!measureType || !activeUnits[measureType]) return `${value} ${units}`

    try {
      const convertedValue = convert(value).from(units).to(activeUnits[measureType])
      return `${convertedValue.toFixed(2)} ${activeUnits[measureType]}`
    } catch (error) {
      console.warn(`Could not convert ${value} from ${units} to ${activeUnits[measureType]}`)
      return `${value} ${units}`
    }
  }, [value, units, activeUnits])

  const handlePdfClick = () => {
    console.log(`PDF clicked: ${PDFPath}`)
    // Future implementation will handle opening the PDF
  }

  return (
    <View style={styles.container} testID={testId}>
      <View style={styles.labelValuePair}>
        <Tooltip title={`${labelDisplayName}`}>
          <Text
            style={[styles.label, { color: theme.colors.onSurfaceVariant }]}
            accessibilityRole="text"
            numberOfLines={1}
          >
            {labelDisplayName}:
          </Text>
        </Tooltip>

        <View style={styles.inputContainer}>
          <Tooltip title={`${labelDisplayName}: ${displayValue}`}>
            <TextInput
              value={String(displayValue)}
              editable={false}
              mode="outlined"
              dense
              style={[styles.input, { backgroundColor: theme.colors.elevation.level1 }]}
              contentStyle={styles.inputContent}
              accessibilityRole="text"
              accessibilityLabel={`${labelDisplayName} value`}
            />
          </Tooltip>

          {PDFPath && PDFPath.trim() !== "" && (
            <Tooltip title="View PDF document">
              <IconButton
                icon={() => <FilePdf size={16} color={theme.colors.primary} />}
                size={16}
                onPress={handlePdfClick}
                style={styles.pdfIcon}
              />
            </Tooltip>
          )}
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: 300, // Fixed width for consistent layout
    minHeight: 32,
    marginBottom: 4,
    marginRight: 8,
  },
  labelValuePair: {
    flexDirection: "row",
    alignItems: "center",
    height: "100%",
  },
  label: {
    fontSize: 12,
    width: 120, // Fixed width for labels
    flexShrink: 0,
    paddingRight: 8,
    textAlign: "right", // Right align labels
  },
  inputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  input: {
    flex: 1,
    height: 28,
    maxWidth: 140, // Fixed max width for input
  },
  inputContent: {
    fontSize: 12,
    paddingVertical: 2,
    height: 28,
  },
  pdfIcon: {
    margin: 0,
    padding: 0,
    width: 20,
    height: 20,
  },
})

